<template>
    <ui-overlay-load v-if="loader.news || loader.category" background-color-overlay="transparent" />
    <div v-else>
        <span class="back" @click="backList"> <i class="fal fa-chevron-left" />Voltar </span>
        <section>
            <article>
                <div class="content">
                    <h2>{{ getSelectedNews.title }}</h2>
                    <div
                        v-if="getSelectedNews.cover != null"
                        class="cover"
                        :style="getHighlightedStyle"
                    >
                        <div v-if="news.selectedCategoryName" class="category">
                            {{ news.selectedCategoryName }}
                        </div>
                    </div>
                    <!-- Use this to correctly display content made in ui-text-editor -->
                    <div>
                        <div class="ql-snow">
                            <div class="ql-editor" v-add-html="getSelectedNews.article" />
                        </div>
                    </div>
                    <!-- Content end -->
                </div>
                <div class="content-footer">
                    <div class="info">
                        <p>{{ getSelectedNews.created_at | moment("DD/MM/YYYY [às] HH:mm") }}</p>
                    </div>
                </div>
                <div v-if="getSimilarNews.length > 0" class="read-more">
                    <div class="header">
                        <h2>Leia Também:</h2>
                        <span @click="backList">Ver todos</span>
                    </div>
                    <div v-for="news in getSimilarNews" :key="news.id">
                        <div class="card" @click="viewNews(news.id)">
                            <div class="image" :style="getCover(news?.cover?.url ? news.cover.url : null)" />
                            <div class="info">
                                <span>{{ news.category.name }}</span>
                                <h3>{{ news.title }}</h3>
                                <p>{{ news.created_at | moment("DD/MM/YYYY [às] HH:mm") }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </article>
            <aside>
                <div v-if="permissions" class="actions">
                    <ui-button design="primary xl block" @click="editNews">
                        Editar Publicação
                    </ui-button>

                    <span class="actions-delete-publication" @click="handleDeletePublication">
                        <i class="fal fa-trash-alt" /> Excluir Publicação
                    </span>
                </div>
                <h3>Categorias</h3>
                <div>
                    <div class="loader-category-list">
                        <ui-overlay-load v-if="loader.categoryList" background-color-overlay="transparent" />
                    </div>
                    <ul>
                        <li v-for="(item, index) in category.list" :key="index" :class="{ 'active': item.id === news.selectedNews.category_id }">
                            <span @click="filterCategoryList(item)">{{ item.name }}</span>
                        </li>
                    </ul>
                </div>
            </aside>
        </section>
        <ui-modal-message
            name="delete-publication"
            service="news"
            title="Deseja realmente excluir esta publicação?"
            text="Cuidado, esta ação não poderá ser desfeita."
            btn-type="danger"
            btn-text="Confirmar"
            icon="fal fa-trash-alt"
            color-circle="danger"
            icon-circle
        />
    </div>
</template>

<script>
import { uiOverlayLoad } from '@/components/ui'
import { uiButton } from '@/components/ui/buttons'
import { news } from '@/services/axios'
import { uiModalMessage } from '@/components/ui/modal'

import { mapGetters } from 'vuex'
import * as userGroups from '@/components/ui-facilita/news/util/userGroups.js'

const defaultLoader = () => ({
	news: true,
	category: true,
	categoryList: true
})

export default {
	name: 'news-main-view',

	components: {
		uiOverlayLoad,
		uiButton,
		uiModalMessage,
	},

	data() {
		return {
			loader: { ...defaultLoader() },

			news: {
				selectedCategoryId: null,
				selectedCategoryName: null,
				selectedNews: null,
				similarNews: null,
			},

			hasPermission: false,
			permissions: false,
			category: {
				list: []
			}
		}
	},

	computed: {
		...mapGetters('auth', { user: 'getUser' }),
		...userGroups,
		getSelectedNews() {
			return this.news.selectedNews
		},
		getSimilarNews() {
			return this.news.similarNews.filter((n) => n.id != this.news.selectedNews.id)
		},
		getHighlightedStyle() {
			return { backgroundImage: `url('${this.getSelectedNews.cover.url}')` }
		},
		getUserPhoto() {
			return { backgroundImage: `url('https://pbs.twimg.com/media/FjU2lkcWYAgNG6d.jpg')` }
		},
		getCover() {
			return (url) => ({ backgroundImage: `url('${url ? url : require('@/assets/imgs/no-image.jpg')}')` })
		},
	},

	watch: {
		$route() {
			// Update
			if (this.$route.params.id) {
				this.getNews()
			}
		},
	},

	async mounted() {
		await this.getNews()
		await this.getNewsCategory()
		await this.getCategory()


		this.setPermissions(this.user.user_group)

		this.$root.$on('news-add', () => {
			this.$router.push({ name: 'news.add' })
		})

		this.$root.$on('set-data-news-deleted', () => {
			this.$router.push({ name: 'news.list' })
		})
	},

	beforeDestroy() {
		this.$root.$off('news-add')
		this.$root.$off('set-data-news-deleted')
	},

	methods: {
		async getNews() {
			try {
				this.loader.news = true
				const response = await news.getAllV2(`${this.$route.params.id}/edit`)
				this.news.selectedCategoryId = response.data.category_id
				this.news.selectedCategoryName = response.data.category.name
				this.news.selectedNews = response.data
				// Check if the user has permission to see the news, he may have accessed
				// the news directly by id, so it is important to check again here
				this.verifyPermissions()
				this.loader.news = false
			} catch {
				// Something went wrong, the news with this id doesn't exist or the API is broken
				this.$toastr.e('Não foi possível carregar a novidade!')
				this.$router.push({ name: 'news.list' })
				this.loader.news = false
			}
		},

        setPermissions(user) {
            switch (user) {
				case 'admin':
				case 'collaborator':
                    this.permissions = true
                    break;				
            }
        },

		verifyPermissions() {
			if (this.isAdminOrCollaborator) {
				this.hasPermission = true
				return
			}

			if (this.news.selectedNews.permissions) {
				this.news.selectedNews.permissions.forEach((team) => {
					if (this.user.team?.length) {
						for (const uTeam of this.user.team) {
							if (parseInt(uTeam.team_id) === parseInt(team.team_id)) this.hasPermission = true
						}

					} else {
						if (team.team_id == this.user.team.id) this.hasPermission = true
					}
				})
			} else this.hasPermission = true

			if (!this.hasPermission) this.$router.push({ name: 'news.list' })
		},

		async getNewsCategory() {
			try {
				this.loader.category = true
				const response = await news.getAllV2(
					`?per_page=100&page=1&category=${this.news.selectedCategoryId}`
				)
				this.news.similarNews = response.data.data
				this.loader.category = false
			} catch {
				this.$toastr.e('Não foi possível carregar as novidades semelhantes!')
				this.loader.category = false
			}
		},
		async getCategory() {
			try {
				this.loader.categoryList = true
				this.category.list = []
				const response = await news.getAllV2('/category')
				this.category.list = response.data
			} catch (e){
				if(e?.message === undefined) {
					return
				}
				this.$toastr.e('Não foi possível carregar as categorias de novidade!')
			} finally {
				this.loader.categoryList = false
			}
		},
		backList() {
			this.$router.push({ name: 'news.list' })
		},
		viewNews(id) {
			this.$router.push({ name: 'news.view', params: { id: id } })
		},
		filterCategoryList(item) {
			this.$router.push({
				name: 'news.list',
				params: { category: item.id }
			})
		},
		handleDeletePublication() {
			this.$root.$emit('modal-msg-open-delete-publication', {
				method: 'delete',
				url: '',
				id: this.$route.params.id,
				msg: 'Não foi possível excluir a novidade, tente novamente!',
				eventSuccess: 'set-data-news-deleted',
			})
		},
		editNews() {
			this.$router.push({
				name: 'news.edit',
				params: { id: this.$route.params.id },
			})
		},
	},
}
</script>

<style lang="stylus" scoped>
@import 'styles.styl';
</style>
